import { GroupByMode, newTaskSuffix } from '@griply/types';
import { getNewTaskPrefill, onLifeAreaReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';

import { useLifeAreaTree } from '../../hooks/use-life-area-tree';
import { GroupedListProps } from '../../types';
import { GoalHeader } from '../goal-header';
import { LifeAreaHeader } from '../life-area-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './life-area-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.LifeArea]: [
    TaskListItemTypes.Task,
    TaskListItemTypes.Goal,
  ],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Task],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const LifeAreaGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );
  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeItems = useLifeAreaTree({
    lifeAreas,
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const lifeAreaIds = useMemo(() => Object.keys(lifeAreasMap), [lifeAreasMap]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onLifeAreaReorder({
      updatedItems,
      oldItems: Object.values(treeItems),
      tasksMap,
      lifeAreaIds,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const lifeAreasMapRef = useRef(lifeAreasMap);
  lifeAreasMapRef.current = lifeAreasMap;

  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const goalsRef = useRef(goals);
  goalsRef.current = goals;

  const lifeAreasRef = useRef(lifeAreas);
  lifeAreasRef.current = lifeAreas;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              hideLifeArea
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.LifeArea,
            groupSectionId,
            groupSetting: groupSettings,
            today,
            defaultNewTaskDate,
          });
          return (
            <NewTaskRow
              lifeArea={prefill.lifeAreaId}
              date={prefill.startDate}
              startTime={null}
              duration={null}
              tags={prefill.tagIds}
            />
          );
        }

        // LifeArea
        if (type === TaskListItemTypes.LifeArea) {
          const lifeArea: LifeArea | undefined =
            lifeAreasMapRef.current[item.sourceId];
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={lifeArea}
              denseFormat={denseFormat}
            />
          );
        }

        // Goal
        if (type === TaskListItemTypes.Goal) {
          return (
            <GoalHeader
              item={itemInstance}
              goals={goalsRef.current}
              lifeAreas={lifeAreasRef.current}
              denseFormat={denseFormat}
            />
          );
        }

        return null;
      },
    [denseFormat, defaultNewTaskDate, groupSettings, today],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeItems}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        dropTypes={dropTypes}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
